import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import './HomePageV3.css';

function HomePageV3() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="homepage-v3">
      {/* Navigation */}
      <nav className={`nav ${scrolled ? 'nav-scrolled' : ''}`}>
        <div className="nav-container">
          <a href="/" className="nav-logo" aria-label="CerebralMachines home"><img src="/CMLogo.png" alt="CerebralMachines" /></a>
          <div className="nav-links">
            <a href="/">Home</a>
            <a href="/platform">Platform</a>
            <a href="/how-it-works">How it Works</a>
            <a href="/use-cases">Use Cases</a>
            <a href="#docs">Docs</a>
            <a href="#blog">Blog</a>
          </div>
          <div className="nav-actions">
            <button className="btn-ghost">Sign In</button>
            <button className="btn-primary">Request a demo</button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-container">
          <motion.div 
            className="hero-left"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="eyebrow">Cerebral Graphs · Enterprise AI that thinks like a brain</div>
            <h1 className="hero-title">A breakthrough innovation for enterprise AI</h1>
            <p className="hero-subtitle">
              The first AI that thinks in graphs, reasons like experts, and adapts like the brain—built for deterministic, auditable decisions at scale.
            </p>
            
            <div className="hero-pills">
              <div className="pill">
                <span className="pill-icon">✓</span>
                100% deterministic decisions on demand
              </div>
              <div className="pill">
                <span className="pill-icon">✓</span>
                Full explainability · No black boxes
              </div>
              <div className="pill">
                <span className="pill-icon">✓</span>
                Massive cross-domain knowledge integration
              </div>
            </div>

            <div className="hero-cta">
              <button className="btn-primary-large">Book a live demo</button>
              <button className="btn-secondary-large">Explore Cerebral Graphs</button>
            </div>

            <p className="hero-muted">
              Built for CIOs, compliance teams, and AI leaders who need results—not experiments.
            </p>
          </motion.div>

          <motion.div 
            className="hero-right"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="hero-usps-card">
              <h3 className="usps-heading">Top 10 Breakthrough Capabilities</h3>
              <ul className="usps-list">
                <li>
                  <span className="usp-icon">✓</span>
                  <span className="usp-text"><strong>100% deterministic AI decisions</strong> — guaranteed, repeatable outcomes with no unpredictability.</span>
                </li>
                <li>
                  <span className="usp-icon">✓</span>
                  <span className="usp-text"><strong>Obsoletes vector search and prompt chaining</strong> — delivers human-brain-like, context-rich reasoning rather than keyword matching.</span>
                </li>
                <li>
                  <span className="usp-icon">✓</span>
                  <span className="usp-text"><strong>Full explainability and auditability</strong> — every decision, node, and path is transparent; zero black-box behavior.</span>
                </li>
                <li>
                  <span className="usp-icon">✓</span>
                  <span className="usp-text"><strong>One unified knowledge graph</strong> — rules, processes, ML models, documents, and context all in a single intelligent system.</span>
                </li>
                <li>
                  <span className="usp-icon">✓</span>
                  <span className="usp-text"><strong>Human-expert-level reasoning</strong> — understands relationships, dependencies, policies, and real-world logic.</span>
                </li>
                <li>
                  <span className="usp-icon">✓</span>
                  <span className="usp-text"><strong>Live ML model adaptation</strong> — update model behavior instantly without retraining or redeployment.</span>
                </li>
                <li>
                  <span className="usp-icon">✓</span>
                  <span className="usp-text"><strong>Massive cross-domain knowledge integration</strong> — clinical, operational, policy, and business data in one reasoning core.</span>
                </li>
                <li>
                  <span className="usp-icon">✓</span>
                  <span className="usp-text"><strong>Ultra-low energy + compute cost</strong> — Platform-powered graphs run dramatically cheaper than LLM-only stacks.</span>
                </li>
                <li>
                  <span className="usp-icon">✓</span>
                  <span className="usp-text"><strong>Rich, multi-layered context formation</strong> — deep temporal, historical, and domain-specific reasoning baked into every decision.</span>
                </li>
                <li>
                  <span className="usp-icon">✓</span>
                  <span className="usp-text"><strong>Real-time system integration</strong> — live streaming updates, continuous learning, and instant graph refresh.</span>
                </li>
              </ul>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Three-Word Band */}
      <section className="three-word-band">
        <div className="band-container">
          <div className="band-card">
            <h3 className="band-label">STUCK AI</h3>
            <p className="band-text">
              Legacy AI pilots stalled in POCs. Disconnected models, brittle rules, opaque decisions.
            </p>
          </div>
          <div className="band-card band-card-highlight">
            <h3 className="band-label">CEREBRAL GRAPHS</h3>
            <p className="band-text">
              One knowledge graph that unifies rules, processes, ML, and documents into a single intelligent system.
            </p>
          </div>
          <div className="band-card">
            <h3 className="band-label">FORWARD</h3>
            <p className="band-text">
              Deterministic outcomes, human-like reasoning, and explainability your auditors will love.
            </p>
          </div>
        </div>
      </section>

      {/* Problem/Solution Section */}
      <section className="problem-solution-section">
        <div className="section-container">
          <div className="section-header-centered">
            <div className="eyebrow">ENTERPRISE-GRADE OUTCOMES</div>
            <h2 className="section-title">Tech to take stuck enterprise AI forward</h2>
            <p className="section-subtitle">
              Enterprise-grade capabilities that deliver real business value and measurable ROI.
            </p>
          </div>

          <div className="features-grid">
            <motion.div 
              className="feature-card"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <div className="feature-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2L2 7l10 5 10-5-10-5z"/>
                  <path d="M2 17l10 5 10-5M2 12l10 5 10-5"/>
                </svg>
              </div>
              <h3 className="feature-title">100% Deterministic Results on Demand</h3>
              <p className="feature-text">
                Control your LLM and decision logic with absolute precision—no leaks, no unpredictability.
              </p>
            </motion.div>

            <motion.div 
              className="feature-card"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <div className="feature-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                  <circle cx="12" cy="12" r="3"/>
                </svg>
              </div>
              <h3 className="feature-title">Absolute Explainability & Full Auditability</h3>
              <p className="feature-text">
                Every decision, every node, every transaction—fully visible, traceable, and transparent.
              </p>
            </motion.div>

            <motion.div 
              className="feature-card"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <div className="feature-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <polyline points="12 6 12 12 16 14"/>
                </svg>
              </div>
              <h3 className="feature-title">End-to-End Process Automation with Human-Like Reasoning</h3>
              <p className="feature-text">
                Encode expert logic, rules, context, and machine learning into a single intelligent graph.
              </p>
            </motion.div>

            <motion.div 
              className="feature-card"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <div className="feature-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="1" x2="12" y2="23"/>
                  <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
                </svg>
              </div>
              <h3 className="feature-title">Massive Savings & Unprecedented Efficiency</h3>
              <p className="feature-text">
                Eliminate re-training loops, reduce compute costs, centralize workflows, and unlock enterprise automation.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Breakthrough Innovation Section */}
      <section className="breakthrough-section">
        <div className="section-container">
          <div className="section-header-centered">
            <div className="eyebrow">BREAKTHROUGH INNOVATION</div>
            <h2 className="section-title">The missing tech in your AI stack is here</h2>
            <p className="section-subtitle">
              Revolutionary capabilities that go beyond traditional AI and machine learning approaches.
            </p>
          </div>

          <div className="breakthrough-cards">
            {[
              {
                title: "One Graph. Ten Functions. Infinite Intelligence.",
                description: "Knowledge, rules, processes, ML models, context, workflows—all linked in a single adaptive graph.",
                matters: "Eliminates data silos and integration headaches"
              },
              {
                title: "Beyond RAG: Human-Brain-Like Knowledge Representation",
                description: "Context-rich, cross-domain reasoning that surpasses vector search and prompt chaining.",
                matters: "Understands relationships, not just keywords"
              },
              {
                title: "Advanced Rule-Based Ontology",
                description: "Expert logic expressed as formal, modular, machine-interpretable rule structures.",
                matters: "Captures institutional knowledge permanently"
              },
              {
                title: "Deterministic Process Representation",
                description: "Codify business processes into repeatable, executable blueprints—right in the graph.",
                matters: "Guarantees consistent outcomes every time"
              },
              {
                title: "Live ML Model Adaptation",
                description: "Update model behavior instantly—no retrains, no redeploys, no downtime.",
                matters: "Adapt to market changes in minutes, not months"
              }
            ].map((item, index) => (
              <motion.div 
                key={index}
                className="breakthrough-card"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="breakthrough-icon">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <path d="M12 6v6l4 2"/>
                  </svg>
                </div>
                <div className="breakthrough-content">
                  <h3 className="breakthrough-title">{item.title}</h3>
                  <p className="breakthrough-text">{item.description}</p>
                  <p className="breakthrough-matters">Why it matters: {item.matters}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Deep Technical Differentiators */}
      <section className="differentiators-section">
        <div className="section-container">
          <div className="section-header-centered">
            <div className="eyebrow">DEEP TECHNICAL DIFFERENTIATORS</div>
            <h2 className="section-title">Advanced capabilities you won't find in any other AI platform</h2>
            <p className="section-subtitle">
              Under the hood, Cerebral Graphs unifies ML, rules, processes, and data into a single Platform-powered graph engine.
            </p>
          </div>

          <div className="differentiators-grid">
            <motion.div 
              className="diff-card"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <div className="diff-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="3"/>
                  <path d="M12 1v6M12 17v6M4.22 4.22l4.24 4.24M15.54 15.54l4.24 4.24M1 12h6M17 12h6M4.22 19.78l4.24-4.24M15.54 8.46l4.24-4.24"/>
                </svg>
              </div>
              <h3 className="diff-title">Machine Learning + Rules + Processes + Data: All in One Graph</h3>
              <ul className="diff-list">
                <li>Symbolic reasoning + cognitive models</li>
                <li>Reusable graph templates</li>
                <li>Process execution as workflows</li>
              </ul>
            </motion.div>

            <motion.div 
              className="diff-card"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <div className="diff-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
                </svg>
              </div>
              <h3 className="diff-title">Massive Cross-Domain Knowledge Integration</h3>
              <ul className="diff-list">
                <li>Clinical, operational, and policy data in one knowledge system</li>
                <li>Rich relationships between entities and events</li>
                <li>Graph-native query and reasoning</li>
              </ul>
            </motion.div>

            <motion.div 
              className="diff-card"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <div className="diff-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
                </svg>
              </div>
              <h3 className="diff-title">Ultra-Low Energy Consumption</h3>
              <ul className="diff-list">
                <li>Efficient models designed for optimized compute usage</li>
                <li>Lower deployment + inference costs</li>
                <li>Up to 80% cheaper than traditional LLM-only stacks</li>
              </ul>
            </motion.div>

            <motion.div 
              className="diff-card"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <div className="diff-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                  <line x1="9" y1="9" x2="15" y2="15"/>
                  <line x1="15" y1="9" x2="9" y2="15"/>
                </svg>
              </div>
              <h3 className="diff-title">Rich, Multi-Layered Context Formation</h3>
              <ul className="diff-list">
                <li>Multi-layered context across processes and timelines</li>
                <li>Historical context and prediction signals</li>
                <li>Domain-specific context modules</li>
              </ul>
            </motion.div>

            <motion.div 
              className="diff-card"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <div className="diff-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
                </svg>
              </div>
              <h3 className="diff-title">Live Integration with Real-Time System Data</h3>
              <ul className="diff-list">
                <li>Stream processors for real-time events</li>
                <li>Graph updates in near-real time</li>
                <li>Continuous learning from live data streams</li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Final CTA Strip */}
      <section className="cta-strip">
        <div className="cta-container">
          <motion.div 
            className="cta-content"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="cta-title">Ready to transform your enterprise with AI?</h2>
            <p className="cta-subtitle">
              Join enterprises using CerebralMachines to automate decisions, improve efficiency, and unlock new insights—without sacrificing control or compliance.
            </p>
            <div className="cta-buttons">
              <button className="btn-primary-large">Start your pilot</button>
              <button className="btn-secondary-large">Talk to an architect</button>
            </div>
            <p className="cta-trust">
              No black boxes · No experimental sandboxes · Just deterministic, explainable AI.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-container">
          <div className="footer-main">
            <div className="footer-brand">
              <a href="/" className="footer-logo" aria-label="CerebralMachines home"><img src="/CMLogo.png" alt="CerebralMachines" /></a>
              <p className="footer-tagline">
                Building the cognitive backbone for enterprise AI.
              </p>
              <div className="footer-social">
                <a href="#" aria-label="LinkedIn">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/>
                    <rect x="2" y="9" width="4" height="12"/>
                    <circle cx="4" cy="4" r="2"/>
                  </svg>
                </a>
                <a href="#" aria-label="Twitter/X">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z"/>
                  </svg>
                </a>
                <a href="#" aria-label="GitHub">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 0 0-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0 0 20 4.77 5.07 5.07 0 0 0 19.91 1S18.73.65 16 2.48a13.38 13.38 0 0 0-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 0 0 5 4.77a5.44 5.44 0 0 0-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 0 0 9 18.13V22"/>
                  </svg>
                </a>
              </div>
            </div>

            <div className="footer-links">
              <div className="footer-column">
                <h4 className="footer-heading">Product</h4>
                <ul>
                  <li><a href="#">Platform</a></li>
                  <li><a href="#">How it Works</a></li>
                  <li><a href="#">Use Cases</a></li>
                  <li><a href="#">Docs</a></li>
                  <li><a href="#">API</a></li>
                </ul>
              </div>

              <div className="footer-column">
                <h4 className="footer-heading">Company</h4>
                <ul>
                  <li><a href="#">About</a></li>
                  <li><a href="#">Team</a></li>
                  <li><a href="#">Careers</a></li>
                  <li><a href="#">Contact</a></li>
                </ul>
              </div>

              <div className="footer-column">
                <h4 className="footer-heading">Resources</h4>
                <ul>
                  <li><a href="#">Blog</a></li>
                  <li><a href="#">Guides</a></li>
                  <li><a href="#">Events</a></li>
                  <li><a href="#">Community</a></li>
                </ul>
              </div>

              <div className="footer-column">
                <h4 className="footer-heading">Legal</h4>
                <ul>
                  <li><a href="#">Privacy Policy</a></li>
                  <li><a href="#">Terms of Service</a></li>
                  <li><a href="#">Security</a></li>
                </ul>
              </div>
            </div>
          </div>

          <div className="footer-bottom">
            <p>&copy; 2025 CerebralMachines. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default HomePageV3;

